import { Close } from "@material-ui/icons";
import { Button, IconButton } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { saveAs } from "file-saver";
import { useSnackbar } from "notistack";
import { ReportApi } from "service/api/report.api";
import { QUERY_KEYS } from "service/constant/query-keys";

export const useGetReportInboundPerformance = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_INBOUND_PERFORMANCE, params],
    queryFn: async () => {
      const res = await ReportApi.getReportInboundPerformance(params);
      const result = res.data?.data?.data;
      if (!result || result.length === 0) return [];

      const totalRow = res.data?.data?.total;
      result?.unshift({
        id: "total",
        contactCenter: "Tổng cộng",
        cityname: "Tổng cộng",
        ...totalRow,
      });

      return result;
    },
    ...options,
  });
};

export const useExportReportInboundPerformance = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_INBOUND_PERFORMANCE_EXPORT, params],
    queryFn: async () => {
      const res = await ReportApi.getReportInboundPerformance(params);
      const linkFile = res.data?.data?.urlFile;
      if (linkFile) {
        const fileName = linkFile.split("/").pop();
        saveAs(linkFile, fileName);
      }
    },
    ...options,
  });
};

export const useGetReportExtension = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_EXTENSION, params],
    retry: false,
    queryFn: async () => {
      const res = await ReportApi.getReportExtension(params);

      const detailReport = res.data.data?.detailReport?.reduce((prev, item) => {
        item.extension.forEach((element) => {
          prev.push({
            contactCenterCode: item.code,
            contactCenterName: item.name,
            extensionName: element.name,
            ...element,
          });
        });
        return prev;
      }, []);
      return { ...res.data.data, detailReport };
    },
    ...options,
  });
};

export const useExportReportExtension = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_EXTENSION_EXPORT, params],
    queryFn: async () => {
      const res = await ReportApi.getReportExtension(params);
      const linkFile = res.data?.data?.urlFile;
      if (linkFile) {
        const fileName = linkFile.split("/").pop();
        saveAs(linkFile, fileName);
      }
    },
    ...options,
  });
};

export const useGetCallLogReports = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.CALL_LOG_REPORT, params],
    queryFn: async () => {
      const res = await ReportApi.getCallLogReports(params);
      return res.data?.data;
    },
    refetchInterval: 10000,
    ...options,
  });
};

export const useGetDetailOutboundCall = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_DETAIL_OUTBOUND_CALL, params],
    queryFn: async () => {
      const res = await ReportApi.getDetailOutboundCall(params);
      return res.data?.data;
    },
    ...options,
  });
};

export const useGetCountDetailOutboundCall = (params, options) => {
  const newParams = { ...params.filter };

  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_DETAIL_OUTBOUND_CALL_COUNT, newParams],
    queryFn: async () => {
      const res = await ReportApi.getCountDetailOutboundCall(newParams);
      return res.data.data;
    },
    // staleTime: Infinity,
    // gcTime: Infinity,
    ...options,
  });
};

export const useExportDetailOutboundCall = (params, options, onClickView) => {
  const newParams = { ...params, isExport: true };
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();

  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_DETAIL_OUTBOUND_CALL_EXPORT, newParams],
    queryFn: async () => {
      await ReportApi.getDetailOutboundCall(newParams);
      enqueueSnackbar(
        "Đã xuất cáo! Vui lòng truy cập lịch sử xuất báo cáo để tải file",
        {
          variant: "success",
          autoHideDuration: 15000,
          action: (snackbarKey) => (
            <>
              <Button
                variant="text"
                onClick={() => {
                  closeSnackbar(snackbarKey);
                  onClickView();
                }}
                sx={{ fontSize: 13, fontWeight: 700 }}
                color="inherit">
                Xem tại đây
              </Button>
              <IconButton
                onClick={() => closeSnackbar(snackbarKey)}
                color="inherit">
                <Close />
              </IconButton>
            </>
          ),
        }
      );
    },
    enabled: false,
    ...options,
  });
};
export const useGetDetailFailedOutboundCall = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_DETAIL_FAILED_OUTBOUND_CALL, params],
    queryFn: async () => {
      const res = await ReportApi.getDetailFailedOutboundCall(params);
      return res.data?.data;
    },
    ...options,
  });
};

export const useExportDetailFailedOutboundCall = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_DETAIL_OUTBOUND_CALL_EXPORT, params],
    queryFn: async () => {
      const res = await ReportApi.getDetailFailedOutboundCall(params);
      const linkFile = res.data?.data;
      if (linkFile) {
        const fileName = linkFile.split("/").pop();
        saveAs(linkFile, fileName);
      }
    },
    ...options,
  });
};
export const useGetDetailFailedInboundCall = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_DETAIL_FAILED_INBOUND_CALL, params],
    queryFn: async () => {
      const res = await ReportApi.getDetailFailedInboundCall(params);
      return res.data?.data;
    },
    ...options,
  });
};

export const useExportDetailFailedInboundCall = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_DETAIL_INBOUND_CALL_EXPORT, params],
    queryFn: async () => {
      const res = await ReportApi.getDetailFailedInboundCall(params);
      const linkFile = res.data?.data;
      if (linkFile) {
        const fileName = linkFile.split("/").pop();
        saveAs(linkFile, fileName);
      }
    },
    ...options,
  });
};

export const useGetDetailInboundCall = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_DETAIL_INBOUND_CALL, params],
    queryFn: async () => {
      const res = await ReportApi.getDetailInboundCall(params);
      return res.data?.data;
    },
    ...options,
  });
};

export const useGetCountDetailInboundCall = (params, options) => {
  const newParams = { ...params.filter };

  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_DETAIL_INBOUND_CALL_COUNT, newParams],
    queryFn: async () => {
      const res = await ReportApi.getCountDetailInboundCall(newParams);
      return res.data.data;
    },
    // staleTime: Infinity,
    // gcTime: Infinity,
    ...options,
  });
};

export const useExportDetailInboundCall = (params, options, onClickView) => {
  const newParams = { ...params, isExport: true };
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();

  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_DETAIL_INBOUND_CALL_EXPORT, newParams],
    queryFn: async () => {
      await ReportApi.getDetailInboundCall(newParams);
      enqueueSnackbar(
        "Đã xuất cáo! Vui lòng truy cập lịch sử xuất báo cáo để tải file",
        {
          variant: "success",
          autoHideDuration: 15000,
          action: (snackbarKey) => (
            <>
              <Button
                variant="text"
                onClick={() => {
                  closeSnackbar(snackbarKey);
                  onClickView();
                }}
                sx={{ fontSize: 13, fontWeight: 700 }}
                color="inherit">
                Xem tại đây
              </Button>
              <IconButton
                onClick={() => closeSnackbar(snackbarKey)}
                color="inherit">
                <Close />
              </IconButton>
            </>
          ),
        }
      );
    },
    enabled: false,
    ...options,
  });
};

export const useGetReportOutboundCallByExt = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_OUTBOUND_CALL_BY_EXT, params],
    queryFn: async () => {
      const res = await ReportApi.getReportOutboundCallByExt(params);
      return res.data?.data;
    },
    ...options,
  });
};

export const useExportReportOutboundCallByExt = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_OUTBOUND_CALL_BY_EXT_EXPORT, params],
    queryFn: async () => {
      const res = await ReportApi.getReportOutboundCallByExt(params);
      const linkFile = res.data?.data;
      if (linkFile) {
        const fileName = linkFile.split("/").pop();
        saveAs(linkFile, fileName);
      }
    },
    ...options,
  });
};

export const useGetReportExtNotCall = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_EXT_NOT_CALL, params],
    queryFn: async () => {
      const res = await ReportApi.getReportExtNotCall(params);
      return res.data?.data;
    },
    ...options,
  });
};

export const useExportReportExtNotCall = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_EXT_NOT_CALL_EXPORT, params],
    queryFn: async () => {
      const res = await ReportApi.getReportExtNotCall(params);
      const linkFile = res.data?.data;
      if (linkFile) {
        const fileName = linkFile.split("/").pop();
        saveAs(linkFile, fileName);
      }
    },
    ...options,
  });
};
export const useGetReportInboundCallByExt = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_INBOUND_CALL_BY_EXT, params],
    queryFn: async () => {
      const res = await ReportApi.getReportInboundCallByExt(params);
      return res.data?.data;
    },
    ...options,
  });
};

export const useExportReportInboundCallByExt = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_INBOUND_CALL_BY_EXT_EXPORT, params],
    queryFn: async () => {
      const res = await ReportApi.getReportInboundCallByExt(params);
      const linkFile = res.data?.data;
      if (linkFile) {
        const fileName = linkFile.split("/").pop();
        saveAs(linkFile, fileName);
      }
    },
    ...options,
  });
};
export const useGetReportInboundCallByUnit = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_INBOUND_CALL_BY_UNIT, params],
    queryFn: async () => {
      const res = await ReportApi.getReportInboundCallByUnit(params);
      return res.data?.data;
    },
    ...options,
  });
};

export const useExportReportInboundCallByUnit = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_INBOUND_CALL_BY_UNIT_EXPORT, params],
    queryFn: async () => {
      const res = await ReportApi.getReportInboundCallByUnit(params);
      const linkFile = res.data?.data;
      if (linkFile) {
        const fileName = linkFile.split("/").pop();
        saveAs(linkFile, fileName);
      }
    },
    ...options,
  });
};

export const useGetReportOutboundCallByUnit = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_OUTBOUND_CALL_BY_UNIT, params],
    queryFn: async () => {
      const res = await ReportApi.getReportOutboundCallByUnit(params);
      return res.data?.data;
    },
    ...options,
  });
};

export const useExportReportOutboundCallByUnit = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_OUTBOUND_CALL_BY_UNIT_EXPORT, params],
    queryFn: async () => {
      const res = await ReportApi.getReportOutboundCallByUnit(params);
      const linkFile = res.data?.data;
      if (linkFile) {
        const fileName = linkFile.split("/").pop();
        saveAs(linkFile, fileName);
      }
    },
    ...options,
  });
};

export const useGetReportSynthesisFailedCall = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_SYNTHESIS_FAILED_CALL, params],
    queryFn: async () => {
      const res = await ReportApi.getReportSynthesisFailedCall(params);
      return res.data?.data;
    },
    ...options,
  });
};

export const useExportReportSynthesisFailedCall = (params, options) => {
  return useQuery({
    queryKey: [QUERY_KEYS.REPORT_SYNTHESIS_FAILED_CALL_EXPORT, params],
    queryFn: async () => {
      const res = await ReportApi.getReportSynthesisFailedCall(params);
      const linkFile = res.data?.data;
      if (linkFile) {
        const fileName = linkFile.split("/").pop();
        saveAs(linkFile, fileName);
      }
    },
    ...options,
  });
};
